/* Angular Component */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { Router } from "@angular/router";
import { MediaInfo, MediaItem, MediaType } from "../../../domain/media";
import { MediaRepository } from "../../../domain/media-repository";
import { UserService } from "../../../domain/user-service";
import { Subscription } from "rxjs";
import { Socket } from "../../../infrastructure/socket.service";
import { ToastrService } from "ngx-toastr";
import { User } from "../../../domain/user";
import { DomSanitizer } from "@angular/platform-browser";
import { IMediaDependencyEntry } from "./models/IMediaDependencyEntry";
import {
  SchedulerDependencyEntryViewModel,
  MediaDependencyEntryViewModel,
} from "./models/MediaDependencyEntryViewModel";
import { ResolveActionResult } from "./models/ResolveActionResult";
import { ShowRepository } from "../../../domain/show-repository";
import { ArraysService } from "../../../services/arrays.service";
import {
  AlertParagraphText,
  IUIServiceButton,
} from "../../common/alert/alert.component";
import { UI } from "../../../services/ui.service";
import moment from "moment";
import { ShowMetaData } from "../../../domain/show";
import { OriginMetadata } from "../../../domain/origin-metadata";

@Component({
  selector: "library-item",
  templateUrl: "./libraryItem.component.html",
  styleUrls: ["./libraryItem.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LibraryItemComponent implements OnInit, OnDestroy {
  @Input()
  item: MediaItem;

  @Input()
  viewMode = "gallery";

  @Input()
  isQCAllowed = false;

  mediaQualityChangedSubscription: Subscription;

  menuOpen = false;
  dragged = false;
  isRecording = false;
  showMultipleDelete = false;

  @Input()
  showMenuButtons = true;

  channelFramerate: number;
  mediaUpdatedSubscription: Subscription;
  packshotUploadedSubscription: Subscription;

  deleteItems = [];

  currentUser: User;
  constructor(
    private ref: ElementRef,
    private mediaRepository: MediaRepository,
    private ui: UI,
    private router: Router,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
    private socketService: Socket,
    private toastr: ToastrService,
    private _sanitizer: DomSanitizer,
    private _showRepository: ShowRepository,
    private _arraysService: ArraysService,
  ) {}

  ngOnInit() {
    this.currentUser = this.userService.getCurrentUser();
    this.ref.nativeElement.addEventListener(
      "dragstart",
      this.onDragStart.bind(this),
      false,
    );
    this.ref.nativeElement.addEventListener(
      "dragend",
      this.onDragEnd.bind(this),
      false,
    );

    this.userService.getCurrentChannel().then((currentChannel) => {
      this.channelFramerate = currentChannel ? currentChannel.framerate : null;
    });

    this.mediaQualityChangedSubscription =
      this.socketService.mediaQualityChanged.subscribe((e) => {
        if (this.item.id !== e.mediaId) {
          return;
        }
        this.item.quality = e.quality;
        this.changeDetectorRef.markForCheck();
      });

    this.mediaUpdatedSubscription = this.mediaRepository.mediaUpdated.subscribe(
      this.onMediaUpdate.bind(this),
    );
    this.packshotUploadedSubscription =
      this.socketService.packshotUploadFinishedChange.subscribe(
        this.onPackshotUploaded.bind(this),
      );
    this.isRecording = this.item.recording;
  }

  ngOnDestroy() {
    if (this.mediaUpdatedSubscription) {
      this.mediaUpdatedSubscription.unsubscribe();
    }

    if (this.mediaQualityChangedSubscription) {
      this.mediaQualityChangedSubscription.unsubscribe();
    }
  }

  onMediaUpdate(mediaInfo: MediaInfo) {
    if (this.item.id === mediaInfo.id) {
      this.item = mediaInfo;
      this.changeDetectorRef.markForCheck();
    }
  }

  onPackshotUploaded(uploadResult) {
    if (uploadResult.mediaId === this.item.id && uploadResult.uploadSuccess) {
      this.item.contentSnapshotUrl = uploadResult.url;
      this.changeDetectorRef.markForCheck();
    }
  }

  onDragStart(event) {
    this.dragged = true;
    event.dataTransfer.setData("text/media", JSON.stringify(this.item));
    event.dataTransfer.dropEffect = "copy";
  }

  onDragEnd(event) {
    this.dragged = false;
  }

  /**
   * Delete Media
   * @return void
   */
  deleteMedia(event) {
    /* Start Multiple Delete Flow */
    if (event && event.shiftKey) {
      this.deleteItems.push(this.item);
      this.showMultipleDelete = true;

      return;
    }

    this.ui.notify(this.ui.EVENTS.ShowConfirm, {
      title: "Delete Media Item",
      message: "Are you sure about it?",
      buttons: [
        {
          title: "Yes",
          callback: this.onDeleteConfirmed.bind(this),
        },
        {
          title: "No",
          callback: this.onDeleteCancelled.bind(this),
        },
      ],
    });
  }

  /**
   * Dispatched when delete operation is confirmed
   * @return void
   */
  onDeleteConfirmed() {
    this.mediaRepository
      .deleteMedia(this.item.id)
      .then(() => this.onDeleteSuccess())
      .catch((err) => {
        console.log("onDeleteConfirmed-> ", err);
        const bottomTexts: AlertParagraphText[] = [];
        const buttons: IUIServiceButton[] = [
          {
            title: "OK",
            callback() {},
          },
        ];
        let message = "An error occurred while trying to delete media";
        if (err.status === 400) {
          message = err.error.message;
        } else if (err.status === 409) {
          //dependency conflict
          const allDependencies = this._mapDependenciesViewModels(
            err.error as IMediaDependencyEntry[],
          );
          message = this._initMediaDependenciesTableHtml(allDependencies);
          this._initMediaDependenciesResolveButtons(
            buttons,
            bottomTexts,
            allDependencies,
          );
        }

        this.ui.notify(this.ui.EVENTS.ShowConfirm, {
          title: "Delete Media",
          message: this._sanitizer.bypassSecurityTrustHtml(message),
          buttons: buttons,
          bottomTexts: bottomTexts,
        });
      });
  }

  /**
   * Delete was cancelled, this method can be used for G.A propose
   * @return void
   */
  onDeleteCancelled() {}

  /**
   * Starting Preview Flow
   * @return void
   */
  previewMedia() {
    this.ui.notify(this.ui.EVENTS.OpenVideoPreview, {
      mediaId: this.item.id,
      snapshot: this.item.contentSnapshotUrl,
      startTime: 0,
    });
  }

  onDeleteSuccess() {
    this.ref.nativeElement.remove();
  }

  editMedia() {
    const $filter = document.body.querySelector("filter");
    if ($filter) {
      this.ui.setCssProperty($filter, { transform: "translateX(-300px)" });
    }
    this.router.navigate(["/mam/editMedia", this.item.id], {
      queryParamsHandling: "preserve",
    });
  }

  formatDuration() {
    if (!this.item.contentDuration) {
      return "N/A";
    }

    return this.item.formatDuration(this.channelFramerate);
  }

  gotoQc() {
    this.router.navigate([`mam/qualityCheck/${this.item.id}`]);
  }

  showToastr(message: string) {
    this.toastr.success(message);
  }
  getLibraryItemDefaultImage() {
    switch (this.item.contentMediaType) {
      case MediaType.Audio:
      case MediaType.AudioVideo:
        return "/assets/icons/video-icon.svg";
      default:
        return "/assets/icons/loader-dots.svg";
    }
  }

  private _initMediaDependenciesTableHtml(
    dependencies: MediaDependencyEntryViewModel[],
  ): string {
    let resultHtml: string[] = [];
    //dependencies = dependencies.concat(dependencies).concat(dependencies).concat(dependencies);
    if (dependencies && dependencies.length > 0) {
      resultHtml.push(
        `<div>Can't delete the media. Please resolve these dependencies:</div>`,
      );
      resultHtml.push(
        `<div style="width: 100%;max-height: 50vh;overflow: scroll;display: block">`,
      );
      resultHtml.push('<table style="width: 100%">');
      resultHtml.push(
        '<tr><th style="border: 1px solid black">Source Type</th><th  style="border: 1px solid black">Source Name</th><th  style="border: 1px solid black">Start Time UTC</th><th  style="border: 1px solid black">Duration</th></tr>',
      );

      for (const dependency of dependencies) {
        if (
          dependency.HasEditSchedulerPermission === false ||
          dependency.IsAutomaticResolveByConvertToContractDoable === false ||
          dependency.IsAutomaticResolveByRemovingShowDoable === false
        ) {
          resultHtml.push(`<tr style="background-color: #f79090d9">`);
        } else {
          resultHtml.push(`<tr>`);
        }

        resultHtml.push(
          `<td style="border: 1px solid black">${dependency.SourceType}</td><td  style="border: 1px solid black">${dependency.SourceName}</td><td  style="border: 1px solid black">${dependency.StartTimeUTC}</td><td  style="border: 1px solid black">${dependency.Duration}</td></tr>`,
        );
      }
      resultHtml.push("</table>");
      resultHtml.push(`</div>`);
    }
    return resultHtml.join("");
  }

  private _initMediaDependenciesResolveButtons(
    buttons: IUIServiceButton[],
    bottomTexts: AlertParagraphText[],
    dependencies: MediaDependencyEntryViewModel[],
  ): void {
    const schedulerDependencies = dependencies.filter(
      (d) => d instanceof SchedulerDependencyEntryViewModel,
    ) as SchedulerDependencyEntryViewModel[];
    if (dependencies?.length > 0) {
      if (
        dependencies.every(
          (dependency) =>
            dependency.HasEditSchedulerPermission &&
            dependency.IsAutomaticResolveByRemovingShowDoable,
        )
      ) {
        buttons.push({
          title: "Resolve By Removing Shows",
          callback: async () => {
            await this._resolveCallbackHandler(
              buttons,
              bottomTexts,
              schedulerDependencies,
              EResolveActionType.ResolveByDeletingShows,
            );
          },
        });
      }
      if (
        dependencies.every(
          (dependency) =>
            dependency.HasEditSchedulerPermission &&
            dependency.IsAutomaticResolveByConvertToContractDoable,
        )
      ) {
        buttons.push({
          title: "Resolve By Creating Contracts",
          callback: async () => {
            await this._resolveCallbackHandler(
              buttons,
              bottomTexts,
              schedulerDependencies,
              EResolveActionType.ResolveByCreatingContracts,
            );
          },
        });
      }

      if (
        dependencies.some(
          (d) =>
            d.HasEditSchedulerPermission === false ||
            d.IsAutomaticResolveByRemovingShowDoable === false ||
            d.IsAutomaticResolveByConvertToContractDoable === false,
        )
      ) {
        bottomTexts.push(
          new AlertParagraphText(
            `Please resolve the marked dependencies to unlock all action buttons.`,
            { color: "#f79090d9" },
          ),
        );
      }
    }
  }
  private _mapDependenciesViewModels(
    dependencies: IMediaDependencyEntry[],
  ): MediaDependencyEntryViewModel[] {
    const result = [];

    for (let dependency of dependencies) {
      let hasEditSchedulerPermission = true;
      let isAutomaticResolveByRemovingShowDoable = false;
      let isAutomaticResolveByConvertToContractDoable = false;

      if (dependency.SourceType === "Channel") {
        hasEditSchedulerPermission =
          this.currentUser.hasEditSchedulePermissionBy(
            dependency.ChannelId,
            false,
          );
        isAutomaticResolveByRemovingShowDoable = true;
        isAutomaticResolveByConvertToContractDoable = true;
        const showStartTime = moment.utc(
          dependency["Show"]["Schedule"]["StartTime"],
        );
        const showDurationMs: number = dependency["Show"]["ShowDurationMs"];
        const showSomMs: number =
          dependency["Show"]["VideoDefinition"]["SomMs"];
        const showName: string = dependency["Show"]["Name"];
        let originMetaData: OriginMetadata = null;
        if (dependency["Show"]["OriginMetadata"]) {
          originMetaData = new OriginMetadata(
            dependency["Show"]["OriginMetadata"]["OriginFilename"],
            dependency["Show"]["OriginMetadata"]["OriginStartTime"],
          );
        }
        const showMetaData: ShowMetaData = new ShowMetaData(
          dependency["Show"]["ShowOrigin"],
          originMetaData,
          dependency["Show"]["ShowComment"],
          dependency["Show"]["HouseId"],
          dependency["Show"]["ReconcileKey"],
          dependency["Show"]["SubtitleLangsToCheck"],
        );
        const isAnchor = dependency["Show"]["IsAnchored"];
        result.push(
          new SchedulerDependencyEntryViewModel(
            dependency.SourceType,
            dependency.SourceName,
            dependency.StartTimeUTC,
            dependency.Duration,
            hasEditSchedulerPermission,
            isAutomaticResolveByRemovingShowDoable,
            isAutomaticResolveByConvertToContractDoable,
            dependency.ChannelId,
            dependency.ShowGuid,
            showStartTime,
            showDurationMs,
            showSomMs,
            showName,
            isAnchor,
            showMetaData,
          ),
        );
      } else if (dependency.SourceType === "Block") {
        hasEditSchedulerPermission = true;
        isAutomaticResolveByRemovingShowDoable = true;
        isAutomaticResolveByConvertToContractDoable = false;
        const showStartTime = moment.utc(
          dependency["Show"]["Schedule"]["StartTime"],
        );
        const showDurationMs: number = dependency["Show"]["ShowDurationMs"];
        const showSomMs: number =
          dependency["Show"]["VideoDefinition"]["SomMs"];
        const showName: string = dependency["Show"]["Name"];
        let originMetaData: OriginMetadata = null;
        if (dependency["Show"]["OriginMetadata"]) {
          originMetaData = new OriginMetadata(
            dependency["Show"]["OriginMetadata"]["OriginFilename"],
            dependency["Show"]["OriginMetadata"]["OriginStartTime"],
          );
        }
        const showMetaData: ShowMetaData = new ShowMetaData(
          dependency["Show"]["ShowOrigin"],
          originMetaData,
          dependency["Show"]["ShowComment"],
          dependency["Show"]["HouseId"],
          dependency["Show"]["ReconcileKey"],
          dependency["Show"]["SubtitleLangsToCheck"],
        );
        const isAnchor = dependency["Show"]["IsAnchored"];
        result.push(
          new SchedulerDependencyEntryViewModel(
            dependency.SourceType,
            dependency.SourceName,
            dependency.StartTimeUTC,
            dependency.Duration,
            hasEditSchedulerPermission,
            isAutomaticResolveByRemovingShowDoable,
            isAutomaticResolveByConvertToContractDoable,
            dependency.ChannelId,
            dependency.ShowGuid,
            showStartTime,
            showDurationMs,
            showSomMs,
            showName,
            isAnchor,
            showMetaData,
          ),
        );
      } else {
        result.push(
          new MediaDependencyEntryViewModel(
            dependency.SourceType,
            dependency.SourceName,
            dependency.StartTimeUTC,
            dependency.Duration,
            hasEditSchedulerPermission,
            isAutomaticResolveByRemovingShowDoable,
            isAutomaticResolveByConvertToContractDoable,
          ),
        );
      }
    }

    return result;
  }
  private async _resolveCallbackHandler(
    actionButtons: IUIServiceButton[],
    bottomTexts: AlertParagraphText[],
    schedulerDependencies: SchedulerDependencyEntryViewModel[],
    actionType: EResolveActionType,
  ): Promise<void> {
    this._showResolvingDependenciesPreloader(actionButtons, bottomTexts);
    /*await new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve(true);
        }, 5000)
      })*/
    let resolveDependenciesResults = [];
    if (actionType === EResolveActionType.ResolveByDeletingShows) {
      resolveDependenciesResults = await this._executeResolveByRemovingShows(
        schedulerDependencies,
      );
    } else if (actionType === EResolveActionType.ResolveByCreatingContracts) {
      resolveDependenciesResults =
        await this._executeResolveByCreatingContracts(schedulerDependencies);
    }

    if (resolveDependenciesResults.every((r) => r.isSuccess) === true) {
      //back to delete media
      console.log(`library-item->_resolveCallbackHandler->Success!`);
      if (actionType === EResolveActionType.ResolveByDeletingShows) {
        this.onDeleteConfirmed();
      }
    } else {
      console.table(resolveDependenciesResults);
      console.error(
        `library-item->_resolveCallbackHandler->Result contains errors`,
        resolveDependenciesResults,
      );
      setTimeout(() => {
        this.ui.notify(this.ui.EVENTS.ShowConfirm, {
          title: "Delete Media",
          message: "Error while deleting media. See console for more logs...",
        });
      }, 500);
    }
  }
  private async _executeResolveByRemovingShows(
    dependencies: SchedulerDependencyEntryViewModel[],
  ): Promise<ResolveActionResult[]> {
    const result = [];

    const dependenciesGroupedByChannelId = this._arraysService.groupBy(
      dependencies,
      "ChannelId",
    );
    for (const channelIdAsText in dependenciesGroupedByChannelId) {
      const channelId = parseInt(channelIdAsText);
      const channelDependencies =
        dependenciesGroupedByChannelId[channelIdAsText];
      try {
        await this._showRepository.deleteChannelShows(
          channelId,
          channelDependencies.map((d) => d.ShowGuid),
        );
        //throw new Error('Test');
        for (const channelDependency of channelDependencies) {
          result.push(new ResolveActionResult(channelDependency, `Success!`));
        }
      } catch (e) {
        for (const channelDependency of channelDependencies) {
          result.push(
            new ResolveActionResult(
              channelDependency,
              `Error while handling _executeResolveByRemovingShows show ${channelDependency.ShowGuid} in channel ${channelId}. Response: ${e}`,
            ),
          );
        }
      }
    }

    return result;
  }

  private async _executeResolveByCreatingContracts(
    schedulerDependencies: SchedulerDependencyEntryViewModel[],
  ): Promise<ResolveActionResult[]> {
    const result = [];
    const deletingMediaInfo = await this.mediaRepository.getMediaInfo(
      this.item.id,
    );

    //create contract
    const newContractId = await this.mediaRepository.createContract();
    const newContractDetails =
      await this.mediaRepository.getMediaInfo(newContractId);
    newContractDetails.tenantMediaId = newContractDetails.uid;
    newContractDetails.metadata = [...deletingMediaInfo.metadata];
    newContractDetails.description = deletingMediaInfo.description;
    newContractDetails.mediaLicenseCountryIds = [
      ...deletingMediaInfo.mediaLicenseCountryIds,
    ];
    newContractDetails.licenseFrom = deletingMediaInfo.licenseFrom;
    newContractDetails.licenseTo = deletingMediaInfo.licenseTo;
    newContractDetails.tags = [...deletingMediaInfo.tags];
    newContractDetails.cropSettings = deletingMediaInfo.cropSettings;
    newContractDetails.startTimeStampMs = deletingMediaInfo.startTimeStampMs;
    newContractDetails.assetTypes = [...deletingMediaInfo.assetTypes];
    newContractDetails.frameFormat = deletingMediaInfo.frameFormat;
    newContractDetails.name = deletingMediaInfo.tenantMediaId + "_deleted";
    await this.mediaRepository.update(newContractDetails);

    const dependenciesGroupedByChannelId = this._arraysService.groupBy(
      schedulerDependencies,
      "ChannelId",
    );
    for (const channelIdAsText in dependenciesGroupedByChannelId) {
      const channelId = parseInt(channelIdAsText);
      const channelDependencies =
        dependenciesGroupedByChannelId[channelIdAsText];
      for (const schedulerDependency of channelDependencies) {
        try {
          //delete original media show
          await this._showRepository.deleteChannelShows(channelId, [
            schedulerDependency.ShowGuid,
          ]);
          //create a show for the new contract
          await this._showRepository.createMediaShowForChannel(
            channelId,
            newContractId,
            schedulerDependency.ShowStartTimeUtc,
            schedulerDependency.ShowSomMs,
            schedulerDependency.ShowDurationMs,
            schedulerDependency.ShowName,
            schedulerDependency.IsShowAnchor,
            schedulerDependency.ShowMetadata,
          );
          result.push(new ResolveActionResult(schedulerDependency, `Success!`));
        } catch (e) {
          result.push(
            new ResolveActionResult(
              schedulerDependency,
              `Error while handling _executeResolveByCreatingContracts show ${schedulerDependency.ShowGuid} in channel ${channelId}. Response: ${e}`,
            ),
          );
        }
      }
    }
    if (result.every((r) => r.isSuccess) === true) {
      //delete the original media
      await this.mediaRepository.deleteMedia(this.item.id);

      //update video id back to original media id
      newContractDetails.tenantMediaId = deletingMediaInfo.tenantMediaId;
      await this.mediaRepository.update(newContractDetails);
      this.onDeleteSuccess();
    }

    return result;
  }
  private _showResolvingDependenciesPreloader(
    actionButtons: IUIServiceButton[],
    bottomTexts: AlertParagraphText[],
  ): void {
    bottomTexts.length = 0;
    bottomTexts.push(
      new AlertParagraphText(`Processing...`, { "text-align": "center" }),
    );
    for (let actionButton of actionButtons) {
      actionButton.disabled = true;
    }
  }
}
enum EResolveActionType {
  ResolveByDeletingShows,
  ResolveByCreatingContracts,
}
